import {
  User,
  AlertTriangle,
  ArrowDown,
} from 'lucide-react';

interface Message {
  id: number;
  senderName: string;
  senderAvatarUrl?: string;
  messageText: string;
  aiFlagged: boolean;
  aiToxicityScore?: number;
  severity?: string;
  createdAt: string;
}

interface MessageContextViewerProps {
  messages: Message[];
  flaggedMessageId: number;
}

export function MessageContextViewer({ messages, flaggedMessageId }: MessageContextViewerProps) {
  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-2">
      {messages.map((message, index) => {
        const isFlagged = message.id === flaggedMessageId;
        const showDateSeparator = index === 0 ||
          new Date(messages[index - 1].createdAt).toDateString() !==
          new Date(message.createdAt).toDateString();

        return (
          <div key={message.id}>
            {/* Date separator */}
            {showDateSeparator && (
              <div className="flex items-center justify-center my-3">
                <span className="text-xs text-slate-500 bg-slate-800 px-3 py-1 rounded-full">
                  {new Date(message.createdAt).toLocaleDateString()}
                </span>
              </div>
            )}

            {/* Message */}
            <div
              className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
                isFlagged
                  ? 'bg-red-500/10 border border-red-500/50 ring-2 ring-red-500/30'
                  : 'hover:bg-slate-800/50'
              }`}
            >
              {/* Avatar */}
              {message.senderAvatarUrl ? (
                <img
                  src={message.senderAvatarUrl}
                  alt={message.senderName}
                  className="h-8 w-8 rounded-full flex-shrink-0"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-slate-400" />
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-white text-sm">{message.senderName}</span>
                  <span className="text-xs text-slate-500">{formatTime(message.createdAt)}</span>
                  {isFlagged && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-medium">
                      <AlertTriangle className="h-3 w-3" />
                      Flagged
                    </span>
                  )}
                </div>
                <p className={`text-sm ${isFlagged ? 'text-red-200' : 'text-slate-300'}`}>
                  {message.messageText}
                </p>
                {isFlagged && message.aiToxicityScore !== undefined && (
                  <div className="mt-2 text-xs text-red-400">
                    Toxicity Score: {Math.round(message.aiToxicityScore * 100)}%
                  </div>
                )}
              </div>
            </div>

            {/* Flagged message indicator arrow */}
            {isFlagged && index < messages.length - 1 && (
              <div className="flex justify-center my-1">
                <ArrowDown className="h-4 w-4 text-slate-600" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
